import { useEffect, useState, type ReactNode } from "react";
import SlideOver from "../../../components/slideOver/SlideOver";
import SetupRecordForm from "../../../components/forms/setup/SetupRecordForm";
import { receivingApi } from "../../../shared/receivingApi";
import { useToast } from "../../../contexts/ToastContext";
import type { SetupRecordValues } from "../../../components/forms/setup/SetupRecordForm";
import type { ApiErrorPayload } from "../../../types/api";
import type {
  Warehouse,
  WarehouseLocation,
  WarehouseLocationPayload,
  WarehousePayload,
} from "../../../types/receiving";

interface SlideOverState {
  open: boolean;
  childComponent: ReactNode;
}

const closedPanel: SlideOverState = { open: false, childComponent: null };
const warehouseFields = [
  { name: "name", label: "Warehouse name", placeholder: "Main warehouse", required: true },
  { name: "code", label: "Warehouse code", placeholder: "MAIN", required: true },
];
const locationFields = [
  { name: "name", label: "Location name", placeholder: "Aisle 1, Shelf A", required: true },
  { name: "code", label: "Location code", placeholder: "A1-A", required: true },
];

const apiErrorMessage = (error: ApiErrorPayload, fallback: string) =>
  error.message ?? Object.values(error.errors ?? {}).flat()[0] ?? fallback;

const warehousePayload = (values: SetupRecordValues): WarehousePayload => ({
  name: String(values.name).trim(),
  code: String(values.code).trim(),
  status: Boolean(values.status),
});

export default function Warehouses() {
  const { showSuccess } = useToast();
  const [warehouses, setWarehouses] = useState<Warehouse[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [slideOver, setSlideOver] = useState<SlideOverState>(closedPanel);
  const close = () => setSlideOver(closedPanel);

  const load = async () => {
    setLoading(true);
    setError("");
    const result = await receivingApi.warehouses();
    if (result.success) setWarehouses(result.data);
    else setError(result.data?.message || "Could not load warehouses.");
    setLoading(false);
  };
  useEffect(() => {
    void load();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const openWarehouse = (warehouse: Warehouse | null = null) =>
    setSlideOver({
      open: true,
      childComponent: (
        <SetupRecordForm
          title={warehouse ? "Edit warehouse" : "Add warehouse"}
          description="Warehouse used to organize received inventory."
          record={warehouse}
          fields={warehouseFields}
          onCancel={close}
          onSave={async (values) => {
            const payload = warehousePayload(values);
            const result = warehouse
              ? await receivingApi.updateWarehouse(warehouse.id, payload)
              : await receivingApi.createWarehouse(payload);
            if (!result.success)
              return {
                success: false,
                message: apiErrorMessage(result.data, "Could not save warehouse."),
              };
            showSuccess(warehouse ? "Warehouse updated" : "Warehouse created");
            close();
            await load();
            return { success: true };
          }}
        />
      ),
    });

  const openLocation = (warehouse: Warehouse, location: WarehouseLocation | null = null) =>
    setSlideOver({
      open: true,
      childComponent: (
        <SetupRecordForm
          title={location ? "Edit warehouse location" : "Add warehouse location"}
          description={`${warehouse.name} · ${warehouse.code}`}
          record={location}
          fields={locationFields}
          onCancel={close}
          onSave={async (values) => {
            const payload: WarehouseLocationPayload = warehousePayload(values);
            const result = location
              ? await receivingApi.updateLocation(location.id, payload)
              : await receivingApi.createLocation(warehouse.id, payload);
            if (!result.success)
              return {
                success: false,
                message: apiErrorMessage(result.data, "Could not save location."),
              };
            showSuccess(location ? "Location updated" : "Location created");
            close();
            await load();
            return { success: true };
          }}
        />
      ),
    });

  return (
    <div className="mt-2 px-4 sm:px-6 lg:px-8">
      <div className="card-custom px-4 py-5 sm:flex sm:items-center">
        <div className="sm:flex-auto">
          <h1 className="text-base font-semibold text-gray-900">Warehouses</h1>
          <p className="mt-1 text-sm text-gray-500">
            Manage warehouses and the locations where received products are stored.
          </p>
        </div>
        <button
          onClick={() => openWarehouse()}
          className="mt-4 rounded-md bg-main-purple px-3 py-2 text-sm font-semibold text-white sm:mt-0"
        >
          Add warehouse
        </button>
      </div>
      {error && (
        <p className="mt-4 rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700">
          {error}
        </p>
      )}
      <div className="mt-8 overflow-hidden card-custom">
        {loading ? (
          <p className="p-6 text-sm text-gray-500">Loading warehouses…</p>
        ) : (
          <table className="min-w-full divide-y divide-gray-300">
            <thead>
              <tr>
                <th className="px-6 py-3 text-left text-sm font-semibold">Warehouse</th>
                <th className="px-3 py-3 text-left text-sm font-semibold">Locations</th>
                <th className="px-3 py-3 text-left text-sm font-semibold">Status</th>
                <th />
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {warehouses.length ? (
                warehouses.map((warehouse) => (
                  <tr key={warehouse.id}>
                    <td className="px-6 py-4 align-top">
                      <p className="text-sm font-medium text-gray-900">{warehouse.name}</p>
                      <p className="font-mono text-xs text-gray-500">{warehouse.code}</p>
                    </td>
                    <td className="px-3 py-4">
                      <div className="flex flex-wrap gap-2">
                        {warehouse.locations?.length ? (
                          warehouse.locations.map((location) => (
                            <button
                              key={location.id}
                              onClick={() => openLocation(warehouse, location)}
                              className="rounded-md border border-gray-200 bg-gray-50 px-2.5 py-1.5 text-left hover:bg-gray-100"
                            >
                              <span className="block text-xs font-semibold text-gray-800">
                                {location.name}
                              </span>
                              <span className="font-mono text-xs text-gray-500">
                                {location.code}
                              </span>
                            </button>
                          ))
                        ) : (
                          <span className="text-sm text-gray-500">No locations</span>
                        )}
                        <button
                          onClick={() => openLocation(warehouse)}
                          className="rounded-md border border-dashed border-gray-300 px-2.5 py-1.5 text-xs font-semibold text-main-purple hover:bg-gray-50"
                        >
                          + Add location
                        </button>
                      </div>
                    </td>
                    <td className="px-3 py-4 align-top text-sm text-gray-500">
                      {warehouse.status ? "Active" : "Inactive"}
                    </td>
                    <td className="px-6 py-3 text-right align-top">
                      <button
                        onClick={() => openWarehouse(warehouse)}
                        className="rounded-md bg-main-purple px-3 py-2 text-sm font-semibold text-white"
                      >
                        Edit
                      </button>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={4} className="p-10 text-center text-sm text-gray-500">
                    No warehouses configured.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        )}
      </div>
      <SlideOver
        childComponent={slideOver.childComponent}
        customClass="max-w-md"
        slideOver={slideOver}
        setSlideOver={setSlideOver}
      />
    </div>
  );
}
